import clsx from "clsx";
import { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParamsOfBooking } from "../../hooks/searchParams/useSearchParamsOfBooking";
import { useBookingState } from "../../hooks/useBookingState";
import { useMoney } from "../../hooks/useMoney";
import { usePriceItDisplay } from "../../hooks/usePriceItDisplay";
import { PriceUtils } from "../../utils/PriceUtils";
import { PriceDataHelper } from "./PriceDataHelper";
import { useBookingCard } from "./useBookingCard";
import { ProdAttrUtils } from "../../utils/productAttributeUtils";

const PriceRowArea: React.FC<{
  priceDataHelper: InstanceType<typeof PriceDataHelper>;
  opts?: "CART"[];
}> = ({ priceDataHelper, opts }) => {
  const isPass = priceDataHelper.isPass();
  const priceItDisplay = usePriceItDisplay();
  const { priceIts, fee } = useMemo(
    function () {
      let priceIts: PriceItemDp[] = [];
      let fee = 0;
      for (const { currentBPrice, currentBState } of priceDataHelper.ls) {
        const x = PriceUtils.summary(currentBState, currentBPrice);
        priceIts = PriceUtils.mergeLs([...priceIts, ...x.priceIts]);
        fee += x.fee;
      }
      return {
        priceIts,
        fee,
      };
    },
    [priceDataHelper],
  );

  return (
    <>
      {priceItDisplay
        .dp({ priceIts })
        .map(({ label, key, actualAmount, discountAmount, priceIt }) => {
          return (
            <PriceDetailItem
              opts={opts}
              isPass={isPass}
              key={key}
              label={label}
              value={actualAmount}
              discountAmount={discountAmount}
              priceIt={priceIt}
            />
          );
        })}
      {!!fee && (
        <PriceDetailItem
          opts={opts}
          isPass={isPass}
          key={fee}
          label={"Fee"}
          value={fee}
        />
      )}
    </>
  );
};

export default PriceRowArea;

const PriceDetailItem: FC<{
  label: string;
  value: number;
  discountAmount?: number;
  priceIt?: PriceItemDp;
  isPass: boolean;
  opts?: "CART"[];
}> = ({ label, value, discountAmount, priceIt, isPass, opts }) => {
  const { t } = useTranslation();
  // Pass thì không hiển thị giá SIM
  if (isPass && priceIt?.name === "SIM") return null;
  if (priceIt?.name === "PREMIUM_PLUS") {
    return (
      <>
        {["ADULT", "CHILD"].map((variantType) => {
          const it = priceIt?.details?.filter(
            (x) => x.variantType === variantType,
          );
          const itemCount = it?.reduce(
            (acc, x) => acc + (x.discountAmountSummary.itemCount || 0),
            0,
          );
          if (!itemCount) return null;
          const value = it.reduce(
            (acc, x) => acc + (x.discountAmountSummary.totalBuyWithPass || 0),
            0,
          );
          return (
            <PriceDetailItemDp
              key={variantType}
              opts={opts}
              label={
                t(
                  (value > 0 ? "Premium Plus " : "Premium & Discount ") +
                    (variantType === "ADULT"
                      ? "Adult"
                      : variantType === "CHILD"
                        ? "Child"
                        : variantType),
                ) +
                " x " +
                itemCount
              }
              value={value}
              discountAmount={it.reduce(
                (acc, x) =>
                  acc + (x.discountAmountSummary.totalDiscountApplied || 0),
                0,
              )}
              paName={priceIt?.name}
            />
          );
        })}
      </>
    );
  }

  return (
    <PriceDetailItemDp
      opts={opts}
      label={label}
      value={value}
      discountAmount={discountAmount}
      paName={priceIt?.name}
    />
  );
};

const PriceDetailItemDp: FC<{
  opts?: "CART"[];
  label: string;
  value: number;
  discountAmount?: number;
  paName?: string;
}> = ({ label, value, discountAmount, paName, opts }) => {
  const _CART = !!opts?.includes("CART");
  const { t } = useTranslation();
  const { formatMoney } = useMoney();
  return (
    <div
      className={clsx("price-row flex justify-between", !_CART && "mb-5")}
      data-name={paName}
    >
      <h5 className={_CART ? "min-w-[6rem] text-[#5E6D77]" : "text-sm"}>
        {t(label)}
      </h5>
      <div className="flex items-center gap-2">
        {!!discountAmount && (
          <p className="text-sm font-semibold leading-[1.5rem] text-[#BFC5C9] line-through">
            {formatMoney(value + discountAmount)}
          </p>
        )}
        <p className="ml-1 text-base font-semibold leading-[1.5rem] text-[#232323]">
          {formatMoney(value)}
        </p>
      </div>
    </div>
  );
};

export const PriceDetailSummary: FC<{
  useBookingCardProps: ReturnType<typeof useBookingCard>;
  children?: ReactNode;
}> = ({ children, useBookingCardProps }) => {
  const { t } = useTranslation();
  const { priceDataHelper, priceDataHelperNext } = useBookingCardProps;
  const { editBookingId } = useSearchParamsOfBooking();
  const booking = useBookingState({ mode: "FORM" });
  const isUpdateBooking = booking.id === editBookingId;

  const originalPrice = priceDataHelper.totalOf("TOTAL_BEFORE_DISCOUNT");
  const invidualTotal = priceDataHelper.calcInvidualTotal();

  const totalPrice = priceDataHelper.totalOf("CART_TOTAL");
  const saveOff = priceDataHelper.totalOf("DISCOUNT_AMOUNT");
  const props = {
    originalPrice,
    totalPrice,
    saveOff,
  };
  // Amount already paid in previous order
  const previousPaidAmount =
    priceDataHelperNext.totalOfBeforeServiceFeeTotalOfOrder();
  // Current total amount to pay (including previous paid amount)
  const currentTotalAmount = totalPrice;
  // Difference between current and previous paid amount
  const adjustedAmount = currentTotalAmount - (previousPaidAmount ?? 0);
  const displayTotalPaid = previousPaidAmount ?? 0;
  const displayTotalAmount = currentTotalAmount;

  const upgradedProps = {
    upgradeTotalAmount:
      adjustedAmount >= 0 ? displayTotalAmount : previousPaidAmount,
    upgradeTotalPaid:
      adjustedAmount >= 0 ? displayTotalPaid : currentTotalAmount,
    adjustedAmount: adjustedAmount,
  };

  return (
    <>
      {isUpdateBooking ? (
        <UpgradePriceDetail {...upgradedProps} />
      ) : (
        <div className="flex flex-wrap items-center gap-2 sm:justify-between">
          <NotSelectChildAgeAlertText booking={booking} />
          <OriginalPrice {...props} />
          <SaveOff {...props} />
          <TotalPrice {...props}>{children}</TotalPrice>
          {priceDataHelper.isPass() && (
            <>
              {invidualTotal < totalPrice && (
                <p
                  className="flex w-full justify-start text-xs font-normal leading-[22px] text-[#DA3838] sm:text-sm"
                  title={
                    "Buying each item individually is cheaper (" +
                    invidualTotal +
                    ") " +
                    "than in the package (" +
                    totalPrice +
                    ")"
                  }
                >
                  {t("*Buying each item individually is cheaper")}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

const NotSelectChildAgeAlertText = ({ booking }: { booking: BookingState }) => {
  const { t } = useTranslation();
  const defaultBooka = ProdAttrUtils.findDefaultBooka(
    booking?.bookingAttributes,
  );
  if (defaultBooka?.productAttribute?.name !== "PASS") return null;
  const selected = !!defaultBooka?.value?.ageQuantities?.length;
  if (selected) return null;
  return (
    <span className="text-center text-lg font-semibold leading-[150%] text-[#DE1A1A]">
      {t("Please select the child's age.")}
    </span>
  );
};
const TotalPrice: FC<{
  totalPrice: number;
  children?: ReactNode;
}> = ({ totalPrice, children }) => {
  const { formatMoney } = useMoney();
  const { t } = useTranslation();
  return (
    <h2 className="flex items-center justify-between gap-1 text-[0.875rem] font-normal leading-[1.375rem] text-[#232323] sm:ml-auto">
      <span className="wei-1">{t("Total Amount")}</span>
      <span className="text-[1.25rem] font-semibold leading-[1.625rem]">
        {formatMoney(totalPrice || 0)}
      </span>
      {children}
    </h2>
  );
};

const SaveOff: FC<{
  saveOff: number;
}> = ({ saveOff }) => {
  const { t } = useTranslation();
  const { formatMoney } = useMoney();
  if (!saveOff) return null;
  return (
    <div className="text-right">
      <span className="h-6 content-center rounded-[0.375rem] bg-[#DBE7FF] px-2 py-[0.1875rem] text-[0.75rem] font-normal leading-[1.5rem] text-[#007AFF]">
        {t("Save ", { money: formatMoney(saveOff || 0) })}
      </span>
    </div>
  );
};
const OriginalPrice: FC<{
  originalPrice: number;
  totalPrice: number;
}> = ({ originalPrice, totalPrice }) => {
  const { formatMoney } = useMoney();
  const { t } = useTranslation();
  if (!originalPrice || originalPrice === totalPrice) return null;

  return (
    <h2 className="text-[0.875rem] font-normal leading-[1.375rem] text-[#BFC5C9] sm:flex-1 sm:basis-auto">
      <span className="line-through">{t("Price:")}</span>{" "}
      <span className="text-sm font-semibold line-through">
        {formatMoney(originalPrice)}
      </span>
    </h2>
  );
};

const UpgradePriceDetail: FC<{
  upgradeTotalAmount: number;
  upgradeTotalPaid: number;
  adjustedAmount: number;
}> = ({ upgradeTotalAmount, upgradeTotalPaid, adjustedAmount }) => {
  const { formatMoney } = useMoney();
  const { t } = useTranslation();
  return (
    <div className="shrink-0">
      <h2 className="flex items-center justify-between gap-1 text-[0.875rem] font-normal leading-[1.375rem] text-[#232323] sm:ml-auto">
        <span className="wei-1">{t("Total Amount")}</span>
        <span className="text-[1.25rem] font-semibold leading-[1.625rem]">
          {formatMoney(upgradeTotalAmount || 0)}
        </span>
      </h2>
      <h2 className="flex items-center justify-between gap-1 text-[0.875rem] font-normal leading-[1.375rem] text-[#232323] sm:ml-auto">
        <span className="wei-1">{t("Total Paid")}</span>
        <span className="text-[1.25rem] font-semibold leading-[1.625rem]">
          {formatMoney(upgradeTotalPaid || 0)}
        </span>
      </h2>
      {adjustedAmount >= 0 && (
        <h2 className="flex items-center justify-between gap-1 text-[0.875rem] font-normal leading-[1.375rem] text-[#232323] sm:ml-auto">
          <span className="wei-1">{t("Additional charge")}</span>
          <span className="text-[1.25rem] font-semibold leading-[1.625rem]">
            {formatMoney(adjustedAmount || 0)}
          </span>
        </h2>
      )}
      {adjustedAmount < 0 && (
        <h2 className="flex items-center justify-between gap-1 text-[0.875rem] font-normal leading-[1.375rem] text-[#232323] sm:ml-auto">
          <span className="wei-1">{t("Estimated Refund Amount")}</span>
          <span className="text-[1.25rem] font-semibold leading-[1.625rem]">
            {formatMoney(0 - adjustedAmount)}
          </span>
        </h2>
      )}
    </div>
  );
};
